import type { CalendarViewBooking } from './calendarView.types';

/** Information about a single day in the month grid */
export interface DayInfo {
  day: number;
  isCurrentMonth: boolean;
  date: Date;
}

const SPANISH_MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

/**
 * Returns the Spanish name for a month (1-12).
 */
export function getSpanishMonthName(month: number): string {
  return SPANISH_MONTH_NAMES[month - 1];
}

/**
 * Returns a formatted month header string like "Junio 2025".
 */
export function formatMonthHeader(year: number, month: number): string {
  return `${getSpanishMonthName(month)} ${year}`;
}

/**
 * Navigates to the previous or next month, handling year wrapping.
 * Returns the new {year, month}.
 */
export function navigateMonth(
  year: number,
  month: number,
  direction: 'prev' | 'next'
): { year: number; month: number } {
  if (direction === 'prev') {
    if (month === 1) {
      return { year: year - 1, month: 12 };
    }
    return { year, month: month - 1 };
  }
  // direction === 'next'
  if (month === 12) {
    return { year: year + 1, month: 1 };
  }
  return { year, month: month + 1 };
}

/** Mismo día civil (año-mes-día local). */
export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Avanza o retrocede un día civil (local). */
export function navigateCalendarDay(date: Date, direction: 'prev' | 'next'): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + (direction === 'next' ? 1 : -1));
  return d;
}

/** Etiqueta larga para la barra de día seleccionado (es). */
export function formatSelectedDayLabel(date: Date): string {
  return date.toLocaleDateString('es', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Returns a grid of weeks for the given month.
 * Each week is an array of 7 DayInfo objects (Monday through Sunday).
 * Includes leading days from the previous month and trailing days from the next month.
 */
export function getMonthGrid(year: number, month: number): DayInfo[][] {
  // First day of the month
  const firstOfMonth = new Date(year, month - 1, 1);
  // Last day of the month
  const lastOfMonth = new Date(year, month, 0);
  const daysInMonth = lastOfMonth.getDate();

  // Day of week for the first day (0=Sunday, 1=Monday, ..., 6=Saturday)
  // Convert to Monday-based index (0=Monday, 1=Tuesday, ..., 6=Sunday)
  const firstDayOfWeek = (firstOfMonth.getDay() + 6) % 7;

  const weeks: DayInfo[][] = [];
  let currentWeek: DayInfo[] = [];

  // Add leading days from previous month
  if (firstDayOfWeek > 0) {
    const prevMonthLastDay = new Date(year, month - 1, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      currentWeek.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month - 2, day),
      });
    }
  }

  // Add days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push({
      day,
      isCurrentMonth: true,
      date: new Date(year, month - 1, day),
    });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Add trailing days from next month
  if (currentWeek.length > 0) {
    let nextDay = 1;
    while (currentWeek.length < 7) {
      currentWeek.push({
        day: nextDay,
        isCurrentMonth: false,
        date: new Date(year, month, nextDay),
      });
      nextDay++;
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

/** Día civil del calendario, como medianoche UTC del año-mes-día de la celda. Las OTA se guardan así. */
function utcDayWindow(date: Date): { start: number; end: number } {
  const start = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return { start, end: start + 86_400_000 };
}

/** Día local que ve el operador en la ficha (en Uruguay, la medianoche UTC se ve a las 21:00 del día anterior). */
function localDayWindow(date: Date): { start: number; end: number } {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return { start, end: start + 86_400_000 };
}

function isSystemBooking(booking: {
  bookingOrigin?: CalendarViewBooking['bookingOrigin'];
  createdBy?: string;
}): boolean {
  if (booking.bookingOrigin === 'ota') return false;
  if (booking.bookingOrigin === 'internal') return true;
  return booking.createdBy === 'visitor' || booking.createdBy === 'operator';
}

/**
 * Reservas que ocupan ese día. El checkout no cuenta una vez terminado el intervalo.
 * Las de Interactive Talker se ubican en el día local de la ficha.
 * Las de Airbnb/OTA siguen el día UTC con el que se importan.
 */
export function getBookingsForDay(
  bookings: CalendarViewBooking[],
  date: Date
): CalendarViewBooking[] {
  return bookings.filter((booking) => {
    const start = new Date(booking.startDate).getTime();
    const end = new Date(booking.endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    const day = isSystemBooking(booking) ? localDayWindow(date) : utcDayWindow(date);
    return start < day.end && end > day.start;
  });
}

/** Reserva confirmada en Airbnb (`Reserved`) frente a un bloqueo del calendario (`Not available`). */
export type AirbnbStayKind = 'reserved' | 'block';

type AirbnbStayInput = {
  customerName?: string | null;
  bookingOrigin?: 'internal' | 'ota';
  otaChannel?: string | null;
  createdBy?: string | null;
  externalSource?: string | null;
};

function isAirbnbImport(booking: AirbnbStayInput): boolean {
  if (booking.otaChannel && booking.otaChannel !== 'airbnb') return false;
  if (booking.bookingOrigin === 'ota') return !booking.otaChannel || booking.otaChannel === 'airbnb';
  return (
    booking.createdBy === 'ical_import' ||
    booking.externalSource === 'ical' ||
    booking.externalSource === 'ical_min_stay'
  );
}

/**
 * Distingue una estadía importada de Airbnb de un bloqueo del calendario.
 * Otros canales y las reservas de Interactive Talker devuelven null.
 */
export function airbnbStayKind(booking: AirbnbStayInput): AirbnbStayKind | null {
  if (!isAirbnbImport(booking)) return null;
  const lower = (booking.customerName ?? '').trim().toLowerCase();
  if (
    !lower ||
    lower.includes('not available') ||
    lower.includes('no disponible') ||
    lower.includes('unavailable') ||
    /\bbloqueo\b/.test(lower) ||
    /\bblocked\b/.test(lower)
  ) {
    return 'block';
  }
  return 'reserved';
}

/** Etiqueta corta para la grilla. Null si conviene mostrar el nombre real del huésped. */
export function airbnbStayShortLabel(booking: AirbnbStayInput): string | null {
  const kind = airbnbStayKind(booking);
  if (kind === 'block') return 'Bloqueo';
  if (kind !== 'reserved') return null;
  const lower = (booking.customerName ?? '').trim().toLowerCase();
  const machine =
    !lower ||
    lower === 'reserved' ||
    lower.startsWith('reserved') ||
    /^airbnb\b/.test(lower);
  return machine ? 'Reservado' : null;
}

/** Reserva creada en Interactive Talker (operador o visitante), no importada de una OTA. */
export function isInternalCalendarBooking(booking: {
  bookingOrigin?: CalendarViewBooking['bookingOrigin'];
  createdBy?: string;
}): boolean {
  if (booking.bookingOrigin === 'ota') return false;
  if (booking.bookingOrigin === 'internal') return true;
  return booking.createdBy !== 'ical_import';
}

/**
 * En un mismo día y recurso, la reserva interna tapa a la de Airbnb/OTA.
 * Las internas quedan primero para no caer en el recorte de la celda.
 */
export function preferInternalBookings<
  T extends {
    resourceId: string;
    startDate: string;
    bookingOrigin?: CalendarViewBooking['bookingOrigin'];
    createdBy?: string;
  },
>(bookings: T[]): T[] {
  const internalResources = new Set(
    bookings.filter((booking) => isInternalCalendarBooking(booking)).map((booking) => booking.resourceId)
  );
  return bookings
    .filter((booking) => isInternalCalendarBooking(booking) || !internalResources.has(booking.resourceId))
    .sort((a, b) => {
      const rank = (booking: T) => (isInternalCalendarBooking(booking) ? 0 : 1);
      const byOrigin = rank(a) - rank(b);
      if (byOrigin !== 0) return byOrigin;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
}
